import { Badge, Box, Button, Dialog, Flex, Heading, IconButton, Separator, Text } from '@radix-ui/themes';
import React, { useState } from 'react';
import { CgArrowsExpandUpRight } from 'react-icons/cg';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import ConfirmationDialogue from './ConfirmationDialogue';
import axiosInstance from '../../config/axiosConfig';

const ViewUser = ({ initialValues = null, onActionSuccess }) => {
  const [isViewOpen, setViewOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null); // 'delete' or 'toggleStatus'

  const handleConfirmationOpen = (action) => {
    setConfirmationAction(action);
    setConfirmationOpen(true);
    setViewOpen(false); // Close view dialog to show confirmation on top
  };

  const confirmationDetails = {
    delete: {
      heading: 'Deletion',
      name: initialValues?.userName || 'User',
      action: 'delete',
    },
    toggleStatus: {
      heading: initialValues?.isActive ? 'Deactivation' : 'Activation',
      name: initialValues?.userName || 'User',
      action: initialValues?.isActive ? 'deactivate' : 'activate',
    },
  };

  const handleDelete = async () => {
    try {
      const response = await axiosInstance.delete(`/User/delete-user/${initialValues.token}`);
      if (response.data.result) {
        toast.success('User deleted successfully');
        return true;
      } else {
        toast.error(response.data.errorResponse?.message || 'Failed to delete user');
        return false;
      }
    } catch (error) {
      toast.error('An error occurred while deleting the user.');
      console.error('Error deleting user:', error);
      return false;
    }
  };
  
  const handleToggleStatus = async () => {
    try {
      // Using POST to send a body to the 'delete-user' endpoint as requested
      const response = await axiosInstance.post(`/User/update-user`, {
        token: initialValues.token,
        isActive: !initialValues.isActive,
      });
  
      if (response.data.result) {
        const message = initialValues.isActive ? 'deactivated' : 'activated';
        toast.success(`User ${message} successfully`);
        return true;
      } else {
        toast.error(response.data.errorResponse?.message || 'Failed to update user status');
        return false;
      }
    } catch (error) {
      toast.error('An error occurred while updating user status.');
      console.error('Error updating status:', error);
      return false;
    }
  };


  const handleConfirmAction = async () => {
    let success = false;
    if (confirmationAction === 'delete') {
      success = await handleDelete();
    } else if (confirmationAction === 'toggleStatus') {
      success = await handleToggleStatus();
    }
    
    setConfirmationOpen(false);

    if (success) {
      onActionSuccess?.(); // Refresh the user list in the parent component
    } else {
      setViewOpen(true); // Re-open the details view on failure
    }
  };

  const renderDetail = (label, value) => (
    <Box className="w-[48%] rounded border border-gray-200 px-3 py-2">
      <Heading size="1" color="gray">{label}</Heading>
      <Text size="2">{value || 'N/A'}</Text>
    </Box>
  );

  const roleId = initialValues?.roleId;
  const isDepartmentUser = roleId === 3 || (!!initialValues?.depId && !initialValues?.employeeId);
  const isCaregiverUser = roleId === 4 || !!initialValues?.employeeId;
  const fullName = [initialValues?.firstName, initialValues?.lastName].filter(Boolean).join(' ');

  const dialogContent = isViewOpen ? (
    <Dialog.Content maxWidth="450px">
      <Flex direction={'column'} gap={'4'}>
        <Flex justify="between" align="start">
            <Flex direction={'column'}>
              <Heading size="4">User Details</Heading>
              <Flex direction={'row'} gap={'2'} align="center" mt="1">
                <Text size={'1'}>Status</Text>
                <Separator orientation={'vertical'} />
                <Badge color={initialValues?.isActive ? 'green' : 'tomato'}>
                  <MdOutlineVerifiedUser />
                  {initialValues?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Flex>
            </Flex>
        </Flex>
        
        <Separator size="4" />

        {/* Role-aware User Details */}
        {isDepartmentUser ? (
          <Flex gap="3" wrap="wrap">
            {renderDetail('Department Name', initialValues.firstName)}
            {renderDetail('User Name', initialValues.userName)}
            {renderDetail('Department ID', initialValues.depId)}
            {renderDetail('Department Location', initialValues.depLocation)}
            {renderDetail('Unit Name', initialValues.unitName)}
            {renderDetail('Email', initialValues.email)}
          </Flex>
        ) : isCaregiverUser ? (
          <Flex gap="3" wrap="wrap">
            {renderDetail('Name', fullName)}
            {renderDetail('User Name', initialValues.userName)}
            {renderDetail('Employee ID', initialValues.employeeId)}
            {renderDetail('Department', initialValues.supervisorName || initialValues.supervisor)}
          </Flex>
        ) : (
          <Flex gap="3" wrap="wrap">
            {renderDetail('Name', fullName)}
            {renderDetail('User Name', initialValues.userName)}
            {renderDetail('Email', initialValues.email)}
            {renderDetail('Department ID', initialValues.depId)}
            {renderDetail('Department Location', initialValues.depLocation)}
            {renderDetail('Unit Name', initialValues.unitName)}
          </Flex>
        )}

        <Flex direction={'row'} gap={'2'} align={'center'} justify={'end'} mt="3">
          <Button
            color={initialValues?.isActive ? 'red' : 'green'}
            onClick={() => handleConfirmationOpen('toggleStatus')}
          >
            {initialValues?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <Button color="gray" variant="soft" onClick={() => handleConfirmationOpen('delete')}>
            <RiDeleteBin5Fill /> Delete
          </Button>
        </Flex>
      </Flex>
    </Dialog.Content>
  ) : null;

  return (
    <>
      <Dialog.Root open={isViewOpen} onOpenChange={setViewOpen}>
        <Dialog.Trigger>
          <IconButton variant="solid" onClick={() => setViewOpen(true)}>
            <CgArrowsExpandUpRight width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
        {isViewOpen && createPortal(dialogContent, document.body)}
      </Dialog.Root>

      {confirmationOpen && confirmationAction && (
        <ConfirmationDialogue
          Name={confirmationDetails[confirmationAction].name}
          Type={confirmationDetails[confirmationAction].heading}
          Action={confirmationDetails[confirmationAction].action}
          onCancel={() => {
            setConfirmationOpen(false);
            setViewOpen(true); // Re-open view on cancel
          }}
          onConfirm={handleConfirmAction}
        />
      )}
    </>
  );
};

export default ViewUser;
